import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../api";
import { UnpaidBookingItem, HttpError } from "../../../api/types";
import useShowToast from "../../../hooks/useShowToast";

/**
 * Custom hook to fetch unpaid bookings from backend.
 * @returns {object} - Contains unpaid bookings, loading state, error state, and refetch function.
 */
function useFetchUnpaidBookings() {
  const { t } = useTranslation();
  const [unpaidBookings, setUnpaidBookings] = useState<UnpaidBookingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HttpError | null>(null);
  const { errorToast } = useShowToast();

  const fetchUnpaidBookings = async (
    dateFrom: Date | null,
    dateTo: Date | null,
    username: string | null,
  ) => {
    if (!dateFrom || !dateTo) {
      setUnpaidBookings([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const bookings = await api.getUnpaidBookings(dateFrom, dateTo, username);
      if (api.error) {
        setError(api.error);
        errorToast({
          title: t("club_management.unpaid_hours.fetch.error_title"),
          description: t("club_management.unpaid_hours.fetch.error_description"),
        });
      }
      setUnpaidBookings(bookings || []);
    } finally {
      setLoading(false);
    }
  };

  return {
    unpaidBookings,
    loading,
    error,
    refetchUnpaidBookings: fetchUnpaidBookings,
  };
}

export default useFetchUnpaidBookings;
